import React, { useState, useRef, useEffect, useCallback } from "react";
import { colors } from "../../config/colors";
import axiosInstance from '../../../services/AxiosInstance';

const LocationInput = ({ setUserLocation, setLocationSelected, location }) => {
  const [query, setQuery] = useState(location);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isValid, setIsValid] = useState(true);
  const [loadingSuggestions, setLoadingSuggestionsFlag] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [disableSearch, setDisableSearch] = useState(true);
  const inputRef = useRef(null);
  const svgRef = useRef(null);
  const dropdownRef = useRef(null);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const handleClickOutside = (event) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target) &&
      (svgRef.current && !svgRef.current.contains(event.target))
    ) {
      setTimeout(() => {
        setShowDropdown(false);
      }, 200);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const extractCity = (displayName) => {
    return displayName.split(",")[0].trim();
  };

  const fetchLocationSuggestions = async (city) => {
    try {
      setLoadingSuggestionsFlag(true);
      const response = await axiosInstance.get(`/v1/default/city-suggestions?query=${city}`);
      setSuggestions(response.data.data || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
    } finally {
      setLoadingSuggestionsFlag(false);
    }
  };

  const debouncedFetchLocationSuggestions = useCallback(
    debounce(fetchLocationSuggestions, 1000),
    []
  );

  const handleInputChange = (e) => {
    const city = e.target.value;
    const isValidInput = /^[a-zA-Z0-9\s,.'-]*$/.test(city);

    if (!isValidInput) {
      return;
    }

    if (city?.length < query?.length) {
      setUserLocation('');
    }

    const isActualCitySelectedFromSuggestions = e.target?.value.split(",").length > 1;
    let newQuery = "";
    if (city) {
      setLoadingSuggestionsFlag(true);
      if (isActualCitySelectedFromSuggestions) {
        newQuery = extractCity(city);
      } else {
        newQuery = city;
      }
      setQuery(newQuery);
      setSelectedCity("");
      setActiveIndex(-1);
      setIsValid(false);
      setLocationSelected(false);
    } else {
      setQuery("");
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const selectedCity = extractCity(suggestion.displayName);
    setSelectedCity(selectedCity);
    setQuery(suggestion.displayName);
    setUserLocation(suggestion.displayName);
    setShowDropdown(false);
    setActiveIndex(-1);
    setIsValid(true);
    setLocationSelected(true);
    inputRef.current.focus();
  };

  const handleBlur = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget)) {
      setTimeout(() => setShowDropdown(false), 200);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      return;
    }

    const inputElement = e.target;
    const isAllTextSelected =
      inputElement.selectionStart === 0 &&
      inputElement.selectionEnd === inputElement.value.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const selectedCityIsInSuggestions = (cityName) => {
    return !!suggestions.find(
      (suggestion) => suggestion.displayName === cityName
    );
  };

  useEffect(() => {
    if (query && !disableSearch) {
      if (!selectedCityIsInSuggestions(query)) {
        debouncedFetchLocationSuggestions(extractCity(query));
      }
    }
  }, [query, disableSearch]);

  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const activeElement = dropdownRef.current.querySelector(".active");
      if (activeElement) {
        activeElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, showDropdown]);

  useEffect(() => {
    setQuery(location);
  }, [location]);

  return (
    <div className="relative" onBlur={handleBlur}>
      <div className="relative">
        <input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setDisableSearch(false);
            setShowDropdown(true);
          }}
          className={`w-full px-4 py-2.5 text-sm text-gray-700 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] transition-colors ${
            isValid ? "border-gray-200" : "border-red-500"
          }`}
          placeholder="Enter city"
          aria-label="location"
        />
        <button
          type="button"
          ref={svgRef}
          onClick={() => setShowDropdown(!showDropdown)}
          onFocus={() => {
            setDisableSearch(false);
            setShowDropdown(true);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500 transition-colors"
        >
          <svg
            className={`w-4 h-4 transform transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {!query ? (
            <div className="p-3 text-sm text-gray-500">Type your city</div>
          ) : loadingSuggestions ? (
            <div className="flex justify-center items-center p-4">
              <div className="w-5 h-5 border-2 border-[#166473] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">No suggestions found</div>
          ) : (
            <ul className="py-1">
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    index === activeIndex ? "bg-gray-50" : ""
                  }`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <div className="text-sm text-gray-900">{suggestion.displayName}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
